import React, { useState } from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import ClassIcon from '../icons/ClassIcon';
import PositionIcon from '../icons/PositionIcon';
import RulerVerticalIcon from '../icons/RulerVerticalIcon';
import ScaleIcon from '../icons/ScaleIcon';
import DistanceIcon from '../icons/DistanceIcon';
import FootballHelmetIcon from '../icons/FootballHelmetIcon';
import ArmIcon from '../icons/ArmIcon';
import HandIcon from '../icons/HandIcon';
import LinkIcon from '../icons/LinkIcon';
import RecruitingIcon from '../icons/RecruitingIcon';
import ContactCardIcon from '../icons/ContactCardIcon';
import ProBowlIcon from '../icons/ProBowlIcon';
import StarIcon from '../icons/StarIcon';
import WatchListIcon from '../icons/WatchListIcon';
import UserCircleIcon from '../icons/UserCircleIcon';
import ListItem from '../atoms/ListItem';
import NamedBox from '../atoms/NamedBox';
import TeamLogo from '../atoms/TeamLogo';
import UpdatedWatchList from '../molecules/UpdatedWatchList';
import Links from '../molecules/Links';
import Dialog from '../molecules/Dialog';
import RecruitingContent from '../molecules/RecruitingContent';
import ContactContent from '../molecules/ContactContent';
import { inchesToFeetAndInches, datetimeToDate } from '../services/converter';
import { isValidDOB, getMostRecentDataByProperty, prepareLinks } from '../services/player';
import { COLOR_BLUE, COLOR_DARK_BLUE, COLOR_DARK_GRAY, COLOR_MIDDLE_GRAY, COLOR_BORDER, COLOR_BLACK, COLOR_WHITE } from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import Player from '../types/Player';
import Position from '../types/Position';
import HighSchoolCombine from '../types/HighSchoolCombine';
import HighSchool from '../types/HighSchool';
import Team from '../types/Team';
import NFLTeam from '../types/NFLTeam';
import College from '../types/College';
import User from '../types/User';
import NFLCombine from '../types/NFLCombine';
import { Box, Button, Chip, Grid } from '@material-ui/core';
import { LinkType } from '../types/Link';
import JerseyIcon from '../icons/JerseyIcon';
import NotAvailable from '../atoms/NotAvailable';
import { PlayerOffersResponse } from '../types/Offer';

interface UpdatedPlayerProfileNFLOverviewProps {
  className?: string;
  player?: Player;
  team?: Team;
  selectedPosition?: Position;
  user: User | undefined;
  playerOffers?: PlayerOffersResponse | null;
}

const useStyles = makeStyles((theme: Theme) => ({
  updatedPlayerProfileNFLOverview: {},
  headerClassName: {
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: 0,
  },
  headerName: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(2),
    fontWeight: 700,
  },
  actionWrap: {
    flexShrink: 0,
    display: 'flex',

    '& > button': {
      backgroundColor: 'inherit',
      borderRadius: 0,
      borderLeft: `1px solid ${COLOR_BORDER}`,
      boxShadow: 'none',
      color: COLOR_BLUE,
      padding: theme.spacing(2),

      '& > span': {
        fontSize: theme.typography.pxToRem(16),
        fontWeight: 700,
        textDecoration: 'underline',
        textTransform: 'capitalize',
      },

      '& .MuiSvgIcon-root': {
        color: COLOR_BLACK,
        fontSize: '28px',
        fontWeight: 'normal',
      },
    },

    [theme.breakpoints.down('xs')]: {
      '& > button': {
        minWidth: 0,

        '& .MuiButton-endIcon': {
          margin: 0,
        },
      },
    },
  },
  actionIcon: {
    display: 'flex',
    maxWidth: 28,
    
    '& svg': {
      height: 'auto',
    },

    [theme.breakpoints.down('xs')]: {
      maxWidth: 22,
    },
  },
  actionIconLabel: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  listItem: {
    '& a': {
      color: COLOR_BLUE
    }
  },
  dividedContent: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(.5, 1.5),

    '@media (max-width: 1680px)': {
      gap: theme.spacing(.5, 1),
    },
  },
  dividedContentSection: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(1.5),
    borderRight: `1px solid ${COLOR_MIDDLE_GRAY}`,
    lineHeight: 1.1,

    '&:last-child': {
      paddingRight: 0,
      borderRight: 0,
    },

    '@media (max-width: 1680px)': {
      paddingRight: theme.spacing(1),
    },
  },
  teamWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(.5, 2),
  },
  committedTo: {
    width: '32px',
    height: '32px',
  },
  committedToLink: {
    color: COLOR_BLUE,
    textDecoration: 'underline',
    transition: 'color 0.3s',

    '&:hover': {
      color: COLOR_DARK_BLUE,
    },
  },
  draftedBy: {
    fontWeight: 700,
  },
  draftValue: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontWeight: 700,
    lineHeight: 1,
  },
  progressLine: {
    maxWidth: '350px',
    marginLeft: 'auto',
  },
  chip: {
    borderWidth: '2px',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    height: 26,
  },
  overviewChip: {
    color: COLOR_WHITE,
  },
  chipWithLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  chipLabel: {
    fontWeight: 700,
  },
  column: {
    borderRight: `1px solid ${theme.palette.divider}`,
    "&:last-child": {
      borderRight: "none",
    },
  },
  links: {
    padding: theme.spacing(1, 0),
    height: 'auto',
    alignItems: 'center',
    overflow: 'visible',
  },
  profileLink:{
    filter: 'none',
  },
  socialProfile: {
    marginLeft: theme.spacing(3),

    '&:first-of-type': {
      marginLeft: 0,
    },
  },
  dialog: {
    overflow: 'auto',
  },
  dialogContent: {
    maxWidth: 700,
  },
}), { name: UpdatedPlayerProfileNFLOverview.name });

export default function UpdatedPlayerProfileNFLOverview (props: UpdatedPlayerProfileNFLOverviewProps) {
  const {
    className,
    player,
    selectedPosition,
    user,
    playerOffers
  } = props;
  const classes = useStyles();
  const [isRecruitingDialogOpen, setIsRecruitingDialogOpen] = useState<boolean>(false);
  const [isContactPlayerDialogOpen, setIsContactPlayerDialogOpen] = useState<boolean>(false);

  const primaryNFLTeam = (player?.playerNFLTeams || [])
    .find((nflTeam:NFLTeam) => !!nflTeam?.isPrimary);
  const isPrimaryNFLPrimaryPosition = primaryNFLTeam?.positions?.find(position => position.code === selectedPosition)?.isPrimary || false;
  const nflCombinesWeight = (player?.nflCombines || [])
    .reduce((weight:number, nflCombine:NFLCombine) => {
      return nflCombine?.weight
        ? (nflCombine?.weight > weight ? nflCombine?.weight : weight)
        : weight;
    }, 0);
  const nflCombinesHeight = (player?.nflCombines || [])
    .reduce((height:number, nflCombine:NFLCombine) => {
      return nflCombine?.height
        ? (nflCombine?.height > height ? nflCombine?.height : height)
        : height;
    }, 0);

  const college = ((player && player.playerColleges) || [])
    .find((college:College) => !!college?.isPrimary);
  const primarySchool = ((player && player.highSchools) || [])
    .find((highSchool: HighSchool) => highSchool && highSchool.isPrimary);

  const jerseyNumber = null;
  const highSchoolCombines:HighSchoolCombine[] = (player && player.hsCombines) || [];
  const arm = (highSchoolCombines.find(combine => combine && !!combine.arm) || {}).arm;
  const hand = (highSchoolCombines.find(combine => combine && !!combine.hand) || {}).hand;
  const wingspan = (highSchoolCombines.find(combine => combine && !!combine.wingspan) || {}).wingspan;

  const nflCombines:NFLCombine[] = (player && player.nflCombines) || [];
  const nflWingspan = getMostRecentDataByProperty(nflCombines, 'wingspan');
  const nflArm = getMostRecentDataByProperty(nflCombines, 'arm');
  const nflHand = getMostRecentDataByProperty(nflCombines, 'hand');


  const nflTeamWeight = primaryNFLTeam?.weight;
  const weight = nflTeamWeight || nflCombinesWeight || college?.weight || null;

  const nflTeamHeight = primaryNFLTeam?.height;
  const height = nflTeamHeight || nflCombinesHeight || college?.height || null;

  const dob = !!player?.dob && datetimeToDate(player.dob);
  const links = prepareLinks(player);
  const socialLinks = links.filter(link => link.key === LinkType.instagram || link.key === LinkType.twitter);
  const allLinks = links.filter(link => link.key !== LinkType.instagram && link.key !== LinkType.twitter);
  
  return (
    <NamedBox
      className={clsx(classes.updatedPlayerProfileNFLOverview, className)}
      name='Pro Overview'
      headerNameClassName={classes.headerName}
      headerClassName={classes.headerClassName}
      headerContent={
        <Box className={classes.actionWrap}>
          <Button variant="contained" endIcon={<RecruitingIcon className={classes.actionIcon}/>}
          onClick={() => setIsRecruitingDialogOpen(true)} >
            <span className={classes.actionIconLabel}>Recruiting</span>
          </Button>
          <Button variant="contained" endIcon={<ContactCardIcon className={classes.actionIcon}/>}
          onClick={() => setIsContactPlayerDialogOpen(true)}
          >
            <span className={classes.actionIconLabel}>Contact Player</span>
          </Button>
        </Box>
    }
    >
      <Grid container>
        <Grid item xs={12} md={5} className={classes.column}>
          <ListItem
          icon={ClassIcon}
          title='Draft Year:'
          >
            <div className={classes.dividedContent}>
              <div className={classes.dividedContentSection}>
                {primaryNFLTeam?.draftYear && (
                  <b>{primaryNFLTeam?.draftYear}</b>
                )}
              </div>

              {typeof primaryNFLTeam?.draftRound === 'number' && primaryNFLTeam?.draftRound !== 0 && (
                <>
                  <div className={classes.dividedContentSection}>
                    <div className={classes.draftValue}>
                      Rd {primaryNFLTeam.draftRound}
                    </div>
                  </div>

                  <div className={classes.dividedContentSection}>
                    <div className={classes.draftValue}>
                      Pick {primaryNFLTeam.draftPick}
                    </div>
                  </div>
                </>
              )}

              {(primaryNFLTeam && primaryNFLTeam.draftRound === 0) && (
                <div className={classes.dividedContentSection}>
                  <b>UDFA</b>
                </div>
              )}

              {dob && isValidDOB(dob) && (
                <div className={classes.dividedContentSection}>
                  DOB: <b>{dob}</b>
                </div>
              )}
            </div>
          </ListItem>

          <ListItem icon={PositionIcon} title="Position:" className={classes.listItem}>
            <div className={classes.chipWithLabel}>
              <Chip label={selectedPosition} className={clsx(classes.chip, classes.overviewChip)} color="secondary" />
              {isPrimaryNFLPrimaryPosition && <div className={classes.chipLabel}>(primary)</div>}
            </div>
          </ListItem>

          {primaryNFLTeam?.team?.name && (
            <ListItem
              icon={FootballHelmetIcon}
              title='NFL Team:'
            >
              <div className={classes.teamWrapper}>
                <TeamLogo
                  className={classes.committedTo}
                  team={primaryNFLTeam.team}
                />

                {user?.accessLevel?.accessNFLTeamPage
                  ? <Link
                      to={`/team/${primaryNFLTeam.team.slug}`}
                      className={classes.committedToLink}
                    >
                      <b>{primaryNFLTeam.team.name}</b>
                    </Link>
                  : <b>{primaryNFLTeam.team.name}</b>
                }

                <span className={classes.draftedBy}>(Drafted)</span>
              </div>
            </ListItem>
          )}
          <ListItem icon={JerseyIcon} title="Jersey:" className={classes.listItem}>
            {
              !!jerseyNumber ?
                <Chip label={jerseyNumber} variant="outlined" className={classes.chip} />
              : <NotAvailable />
            }
          </ListItem>
        </Grid>
        <Grid item xs={12} md={3} className={classes.column}>
            <ListItem
              icon={RulerVerticalIcon}
              title={nflTeamHeight ? 'Combine Height:' : 'Team Height:'}
            >
              {!!height ?
              <b>{inchesToFeetAndInches(height)}</b>
              : <NotAvailable />
              }
            </ListItem>

          <ListItem
            icon={ScaleIcon}
            title={nflTeamWeight ? 'Combine Weight:' : 'Team Weight:'}
          >
            {!!weight ? <b>{`${weight} lbs.`}</b> : <NotAvailable />}
          </ListItem>

          <ListItem
            icon={ArmIcon}
            title='Combine Arm:'
          >
            {!!nflArm ? <b>{nflArm}</b> : <NotAvailable />}
          </ListItem>

          <ListItem
            icon={HandIcon}
            title='Combine Hand:'
          >
            {!!nflHand ? <b>{nflHand}</b> : <NotAvailable />}
          </ListItem>

          <ListItem
            icon={DistanceIcon}
            title='Wingspan:'
          >
            {!!nflWingspan ? <b>{nflWingspan}</b> : <NotAvailable />}
          </ListItem>
        </Grid>
        <Grid item xs={12} md={4}>
          <ListItem
            icon={ProBowlIcon}
            title='Pro Bowl:'
          >
            {!!player?.proBowl?.length ? <b>{player.proBowl.join(', ')}</b> : <NotAvailable />}
          </ListItem>

          <ListItem
            icon={StarIcon}
            title='All-Pro:'
          >
            {!!player?.allPro?.length ? <b>{player.allPro.join(', ')}</b> : <NotAvailable />}
          </ListItem>

          <ListItem
            icon={WatchListIcon}
            title='Watch List:'
          >
            {!!player?.watchList?.length ? <UpdatedWatchList list={player.watchList} /> : <NotAvailable />}
          </ListItem>

          <ListItem
            contentClassName={classes.links}
            icon={UserCircleIcon}
            title='Profiles:'
            className={classes.listItem}
          >
            {!!allLinks?.length ? <Links links={allLinks} linkClassName={classes.profileLink}/> : <NotAvailable />}
          </ListItem>
          <ListItem
            contentClassName={classes.links}
            icon={LinkIcon}
            title='Social Profiles:'
            className={classes.listItem}
          >
            {!!socialLinks?.length ? <Links links={socialLinks} linkClassName={classes.socialProfile} /> : <NotAvailable />}
          </ListItem>
        </Grid>

      </Grid>

      {/* {(primaryNFLTeam?.team && (primaryNFLTeam.team.city || primaryNFLTeam.team.state)) && (
        <ListItem
          icon={LocationIcon}
          title='Location:'
        >
          <b>{primaryNFLTeam.team.city}, {primaryNFLTeam.team.state}</b>
        </ListItem>
      )} */}
      <Dialog 
        open={isRecruitingDialogOpen}
        title='Recruiting'
        noHeader={true}
        noContentPadding={true}
        onClose={() => setIsRecruitingDialogOpen(false)}
        className={classes.dialog}
        contentClassName={classes.dialogContent}
      >
        {/* {recruitingContent()} */}
        <RecruitingContent selectedPosition={selectedPosition} playerOffers={playerOffers} primarySchool={primarySchool} player={player} links={links} onClose={() => setIsRecruitingDialogOpen(false)}/>
      </Dialog>

      <Dialog 
        open={isContactPlayerDialogOpen}
        title='Contact Player'
        noHeader={true}
        noContentPadding={true}
        onClose={() => setIsContactPlayerDialogOpen(false)}
        className={classes.dialog}
        contentClassName={classes.dialogContent}
      >
        <ContactContent socialLinks={socialLinks} primarySchool={primarySchool} player={player} links={links} onClose={() => setIsContactPlayerDialogOpen(false)}/>
      </Dialog>
    </NamedBox>
  );
}
//
